import React from 'react';
import type { HistoricalPlayer, Player, Team } from '@/types';
import { getTeamDisplayNameByDate } from '@/utils/team';

type UnifiedTableVariant = 'player' | 'team' | 'historical';

interface UnifiedTableProps {
  variant: UnifiedTableVariant;
  data: Player[] | Team[] | HistoricalPlayer[];
  onRowClick?: (record: Player | Team | HistoricalPlayer) => void;
  className?: string;
  teamNameDate?: string;
}

// 向后兼容的接口
interface HistoricalTableProps {
  data: HistoricalPlayer[];
  onRowClick?: (player: HistoricalPlayer) => void;
}

// 获取变化趋势显示
const getChangeDisplay = (change: number, isRank = false) => {
  const actualChange = isRank ? -change : change; // 排名变化是相反的

  if (actualChange > 0) {
    return {
      color: 'text-positive',
      icon: '↗',
      text: `+${Math.abs(actualChange)}`,
    };
  } else if (actualChange < 0) {
    return {
      color: 'text-negative',
      icon: '↘',
      text: `-${Math.abs(actualChange)}`,
    };
  } else {
    return {
      color: 'text-neutral',
      icon: '—',
      text: '0',
    };
  }
};

const UnifiedTable: React.FC<UnifiedTableProps> = ({
  variant,
  data,
  onRowClick,
  className,
  teamNameDate,
}) => {
  // 渲染表头
  const renderHeader = () => {
    if (variant === 'historical') {
      return (
        <tr className='border-b border-border bg-background-secondary/30'>
          <th className='px-4 py-4 text-left text-sm font-semibold text-text-secondary'>
            排名
          </th>
          <th className='px-4 py-4 text-left text-sm font-semibold text-text-secondary'>
            选手
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            最高ELO
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            创造日期
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            当时战队
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            当前ELO
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            状态
          </th>
        </tr>
      );
    } else if (variant === 'player') {
      return (
        <tr className='border-b border-border bg-background-secondary/30'>
          <th className='px-4 py-4 text-left text-sm font-semibold text-text-secondary'>
            排名
          </th>
          <th className='px-4 py-4 text-left text-sm font-semibold text-text-secondary'>
            选手
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            战队
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            ELO
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            6个月变化
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            状态
          </th>
        </tr>
      );
    } else {
      // team
      return (
        <tr className='border-b border-border bg-background-secondary/30'>
          <th className='px-4 py-4 text-left text-sm font-semibold text-text-secondary'>
            排名
          </th>
          <th className='px-4 py-4 text-left text-sm font-semibold text-text-secondary'>
            战队
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            首发选手平均ELO
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            选手数
          </th>
          <th className='px-4 py-4 text-center text-sm font-semibold text-text-secondary'>
            状态
          </th>
        </tr>
      );
    }
  };

  // 渲染表格行
  const renderRow = (
    record: Player | Team | HistoricalPlayer,
    index: number
  ) => {
    if (variant === 'historical') {
      const player = record as HistoricalPlayer;
      return (
        <tr
          key={player.id}
          className={`transition-colors duration-150 hover:bg-background-secondary/50 ${
            onRowClick ? 'cursor-pointer' : ''
          }`}
          onClick={() => onRowClick?.(player)}
        >
          {/* 排名 */}
          <td className='px-4 py-4'>
            <div className='flex items-center gap-3'>
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                  index < 3
                    ? 'bg-gradient-to-r from-glory-gold to-glory-gold-light text-black'
                    : 'bg-background-secondary text-text-secondary'
                }`}
              >
                {index + 1}
              </div>
            </div>
          </td>
          {/* 选手信息 */}
          <td className='px-4 py-4'>
            <div className='space-y-1'>
              <div className='font-semibold text-text-primary'>
                {player.playerId}
              </div>
              <div className='flex items-center gap-2 text-sm text-text-secondary'>
                <span>{player.realName}</span>
                {player.country && (
                  <>
                    <span>•</span>
                    <span>{player.country}</span>
                  </>
                )}
              </div>
            </div>
          </td>
          {/* 最高ELO */}
          <td className='px-4 py-4 text-center'>
            <div className='font-bold text-lg text-glory-gold'>
              {player.peakElo.toLocaleString()}
            </div>
          </td>
          {/* 创造日期 */}
          <td className='px-4 py-4 text-center text-text-secondary'>
            {new Date(player.peakDate).toLocaleDateString('zh-CN')}
          </td>
          {/* 当时战队 */}
          <td className='px-4 py-4 text-center'>
            <div className='text-text-primary font-medium'>
              {player.teamAtPeak || '-'}
            </div>
          </td>
          {/* 当前ELO */}
          <td className='px-4 py-4 text-center'>
            <div className='text-text-primary font-medium'>
              {player.currentElo.toLocaleString()}
            </div>
            <div
              className={`text-xs ${
                player.currentElo >= player.peakElo
                  ? 'text-positive'
                  : 'text-negative'
              }`}
            >
              {player.currentElo >= player.peakElo
                ? '='
                : `-${(player.peakElo - player.currentElo).toLocaleString()}`}
            </div>
          </td>
          {/* 状态 */}
          <td className='px-4 py-4 text-center'>
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                player.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {player.isActive ? '活跃' : '退役'}
            </span>
          </td>
        </tr>
      );
    } else if (variant === 'player') {
      const player = record as Player;
      const eloChange = getChangeDisplay(player.eloChange6Month || 0);
      const rankChange = getChangeDisplay(player.rankChange6Month || 0, true);

      return (
        <tr
          key={player.id}
          className={`transition-colors duration-150 hover:bg-background-secondary/50 ${
            onRowClick ? 'cursor-pointer' : ''
          }`}
          onClick={() => onRowClick?.(player)}
        >
          {/* 排名 */}
          <td className='px-4 py-4'>
            <div className='flex items-center gap-3'>
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                  player.currentRank <= 3
                    ? 'bg-gradient-to-r from-glory-gold to-glory-gold-light text-black'
                    : 'bg-background-secondary text-text-secondary'
                }`}
              >
                {player.currentRank}
              </div>
            </div>
          </td>
          {/* 选手信息 */}
          <td className='px-4 py-4'>
            <div className='space-y-1'>
              <div className='font-semibold text-text-primary'>
                {player.playerId}
              </div>
              {player.realName && (
                <div className='flex items-center gap-2 text-sm text-text-secondary'>
                  <span>{player.realName}</span>
                  {player.country && (
                    <>
                      <span>•</span>
                      <span>{player.country}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </td>
          {/* 战队 */}
          <td className='px-4 py-4 text-center'>
            <div className='text-text-primary font-medium'>
              {getTeamDisplayNameByDate(
                (player.currentTeam as Team | undefined) ?? null,
                teamNameDate
              )}
            </div>
          </td>
          {/* ELO */}
          <td className='px-4 py-4 text-center'>
            <div className='font-bold text-lg text-glory-gold'>
              {player.currentElo.toLocaleString()}
            </div>
          </td>
          {/* 6个月变化 */}
          <td className='px-4 py-4 text-center'>
            <div className='space-y-1'>
              <div
                className={`flex items-center justify-center gap-1 ${eloChange.color}`}
              >
                <span className='text-base'>{eloChange.icon}</span>
                <span className='font-medium'>{eloChange.text} ELO</span>
              </div>
              {(player.rankChange6Month || 0) !== 0 && (
                <div
                  className={`flex items-center justify-center gap-1 text-xs ${rankChange.color}`}
                >
                  <span>{rankChange.icon}</span>
                  <span>{Math.abs(player.rankChange6Month || 0)} 排名</span>
                </div>
              )}
            </div>
          </td>
          {/* 状态 */}
          <td className='px-4 py-4 text-center'>
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                player.status === 'Active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {player.status === 'Active' ? '活跃' : '退役'}
            </span>
          </td>
        </tr>
      );
    } else {
      // team
      const team = record as Team;
      return (
        <tr
          key={team.id}
          className={`transition-colors duration-150 hover:bg-background-secondary/50 ${
            onRowClick ? 'cursor-pointer' : ''
          }`}
          onClick={() => onRowClick?.(team)}
        >
          {/* 排名 */}
          <td className='px-4 py-4'>
            <div className='flex items-center gap-3'>
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                  index < 3
                    ? 'bg-gradient-to-r from-glory-gold to-glory-gold-light text-black'
                    : 'bg-background-secondary text-text-secondary'
                }`}
              >
                {index + 1}
              </div>
            </div>
          </td>
          {/* 战队信息 */}
          <td className='px-4 py-4'>
            <div className='space-y-1'>
              <div className='font-semibold text-text-primary'>
                {getTeamDisplayNameByDate(team, teamNameDate)}
              </div>
              {team.region && (
                <div className='text-sm text-text-secondary'>{team.region}</div>
              )}
            </div>
          </td>
          {/* 首发选手平均ELO */}
          <td className='px-4 py-4 text-center'>
            <div className='font-bold text-lg text-glory-gold'>
              {team.averageElo
                ? Math.round(team.averageElo).toLocaleString()
                : '计算中'}
            </div>
          </td>
          {/* 选手数 */}
          <td className='px-4 py-4 text-center'>
            <div className='text-text-primary font-medium'>
              {team.players?.length || team.playerCount || 0}
            </div>
          </td>
          {/* 状态 */}
          <td className='px-4 py-4 text-center'>
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                team.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {team.isActive ? '活跃' : '解散'}
            </span>
          </td>
        </tr>
      );
    }
  };
  return (
    <div
      className={`bg-background-secondary rounded-xl border border-border overflow-hidden ${className || ''}`}
    >
      <div className='overflow-x-auto'>
        <table className='min-w-full'>
          <thead>{renderHeader()}</thead>
          <tbody className='divide-y divide-border'>
            {data.map((record, index) => renderRow(record, index))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 向后兼容的HistoricalTable组件
const HistoricalTable: React.FC<HistoricalTableProps> = ({
  data,
  onRowClick,
}) => {
  const handleRowClick = onRowClick
    ? (record: Player | Team | HistoricalPlayer) => {
        onRowClick(record as HistoricalPlayer);
      }
    : undefined;

  return (
    <UnifiedTable
      variant='historical'
      data={data}
      onRowClick={handleRowClick}
    />
  );
};

export default HistoricalTable;
export { UnifiedTable };
